import { ROLE_HOME_TITLES } from "./roleMeta";
import { homePathForRole, isDepartmentRole, isDepartmentForeman } from "./dashboardRole";

/** Mobile bottom-tab priority for single-module / leaf shortcuts (first matches win). */
export const BOTTOM_NAV_PRIORITY = [
  "/sales/loading",
  "/production/packing-register",
  "/stacks",
  "/weighbridge/inward",
  "/weighbridge/outward",
  "/gate/procurement",
  "/purchases",
  "/purchase/reports",
  "/production/boiler",
  "/production/boiler/dashboard",
  "/production/parboiled/dashboard",
  "/production/milling/dashboard",
  "/accounts",
  "/purchase/vouchers",
  "/unloading",
  "/lab/truck",
  "/contracts",
  "/production",
  "/parties",
];

/** Preferred order of main sidebar groups on the phone bottom bar (admin/manager). */
export const BOTTOM_MODULE_PRIORITY = [
  "sales",
  "purchase",
  "weighbridge",
  "gate",
  "inventory",
  "lab",
  "finance",
  "production",
  "master",
  "admin",
];

/** Preferred landing route inside a module (first existing item wins). */
const MODULE_LANDING_PATHS = {
  sales: ["/sales/link", "/sales-contracts", "/sales/loading", "/sales/vouchers", "/sales/reports"],
  purchase: ["/purchases", "/procurement/general", "/contracts", "/unloading", "/purchase/vouchers", "/purchase/reports"],
  weighbridge: ["/weighbridge/inward", "/weighbridge/outward", "/weighbridge/register"],
  gate: ["/gate/procurement", "/gate/outward", "/gate/visitors"],
  inventory: ["/stacks"],
  lab: ["/lab/truck", "/lab/composite", "/lab/plant", "/lab/stacks"],
  finance: ["/accounts/tally-approvals", "/accounts", "/accounts/ledger", "/accounts/tds", "/accounts/bank-reconciliation", "/accounts/tally-mapping", "/labour-warehouse", "/labour-contracts"],
  production: ["/production/packing-register", "/production/reprocessing", "/production", "/production/boiler"],
  master: ["/parties"],
  admin: ["/users", "/audit-logs", "/dropdown-choices"],
};

const MODULE_ICON_KEYS = {
  sales: "purchases",
  purchase: "contracts",
  weighbridge: "weighbridge",
  gate: "gate",
  inventory: "stacks",
  lab: "lab",
  finance: "accounts",
  production: "production",
  master: "parties",
  admin: "users",
};

const MODULE_SHORT_LABELS = {
  sales: "Sales",
  purchase: "Purchase",
  weighbridge: "Weigh",
  gate: "Gate",
  inventory: "Stock",
  lab: "Lab",
  finance: "Finance",
  production: "Prod",
  master: "Master",
  admin: "Admin",
};

export const ROUTE_TITLES = {
  "/": "Dashboard",
  "/daily-report": "Daily mill report",
  "/gate": "Gate",
  "/gate/procurement": "Procurement gate",
  "/gate/outward": "Outward gate",
  "/outward-dispatch": "Sales Link",
  "/sales/link": "Sales Link",
  "/sales/loading": "Loading",
  "/sales/vouchers": "Sales Voucher",
  "/sales/reports": "Sales Reporting",
  "/sales-contracts": "Sales Contract",
  "/gate/visitors": "Visitors",
  "/weighbridge": "Weighbridge",
  "/weighbridge/inward": "Inward weighment",
  "/weighbridge/outward": "Outward weighment",
  "/weighbridge/register": "Weighment register",
  "/contracts": "Contracts",
  "/purchases": "Purchases",
  "/procurement/general": "General procurement",
  "/purchase/reports": "Purchase reporting",
  "/purchase/vouchers": "Purchase vouchers",
  "/lab": "Lab",
  "/lab/truck": "Truck arrival report",
  "/lab/composite": "Composite report",
  "/lab/plant": "In plant report",
  "/lab/stacks": "Stack sample report",
  "/stack-sample-intake": "Stack samples",
  "/stack-sample-approval": "Sample approval",
  "/unloading": "Unloading",
  "/stacks": "Stacks",
  "/production": "Yield log",
  "/production/packing-register": "Packing register",
  "/production/reprocessing": "Reprocessing & blending",
  "/production/boiler": "Boiler workspace",
  "/production/boiler/dashboard": "Boiler dashboard",
  "/production/boiler/register": "Boiler register",
  "/production/parboiled": "Parboiled workspace",
  "/production/parboiled/dashboard": "Parboiled dashboard",
  "/production/milling": "Milling workspace",
  "/production/milling/dashboard": "Milling dashboard",
  "/production/team": "My team",
  "/accounts": "Accounts",
  "/accounts/ledger": "General ledger",
  "/accounts/tds": "TDS register",
  "/accounts/bank-reconciliation": "Bank reconciliation",
  "/accounts/tally-approvals": "Tally approvals",
  "/accounts/tally-mapping": "Tally mapping",
  "/parties": "Parties",
  "/parties/new": "New party",
  "/users": "Users",
  "/audit-logs": "Audit log",
  "/dropdown-choices": "Lookups",
  "/paddy-quality-lab": "Quality lab limits",
  "/login": "Sign in",
};

export function pageTitleForPath(pathname, role) {
  const home = homePathForRole(role);
  if (pathname === home && role && ROLE_HOME_TITLES[role]) {
    return ROLE_HOME_TITLES[role];
  }
  if (pathname === "/" && role && ROLE_HOME_TITLES[role]) {
    return ROLE_HOME_TITLES[role];
  }
  if (role === "production_loading_supervisor") {
    if (pathname === "/sales/loading" || pathname === "/outward-dispatch" || pathname === "/outward-loading") {
      return "Loading";
    }
    if (pathname === "/production/packing-register") return "Packing";
    if (pathname === "/stacks") return "Stock on hand";
  }
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];
  if (pathname.startsWith("/lab/")) return ROUTE_TITLES[pathname] || "Lab";
  if (pathname.startsWith("/weighbridge/")) return ROUTE_TITLES[pathname] || "Weighbridge";
  if (pathname.startsWith("/purchase/")) return ROUTE_TITLES[pathname] || "Purchase";
  if (pathname.startsWith("/purchases/")) return "Purchase";
  if (pathname.startsWith("/sales/")) return ROUTE_TITLES[pathname] || "Sales";
  if (pathname.startsWith("/parties/") && pathname.endsWith("/ledger")) return "Ledger statement";
  if (pathname.startsWith("/parties/") && pathname.endsWith("/edit")) return "Edit party";
  return "Bansal ERP";
}

function homeNavItem(visibleNav, role) {
  const homePath = homePathForRole(role);
  const byPath = new Map(visibleNav.map((n) => [n.to, n]));
  if (homePath === "/") return byPath.get("/");
  return byPath.get(homePath) || {
    to: homePath,
    label: isDepartmentForeman(role) ? "Dashboard" : isDepartmentRole(role) ? "Workspace" : "Home",
    iconKey: isDepartmentForeman(role) ? "dashboard" : "production",
    shortLabel: isDepartmentForeman(role) ? "Dashboard" : isDepartmentRole(role) ? "Workspace" : "Home",
  };
}

/** Leaf shortcuts for single-module roles (loading supervisor, weighbridge, etc.). */
export function pickBottomNavItems(visibleNav, maxQuick = 3, role) {
  const homePath = homePathForRole(role);
  const byPath = new Map(visibleNav.map((n) => [n.to, n]));
  const home = homeNavItem(visibleNav, role);
  const quick = [];
  for (const path of BOTTOM_NAV_PRIORITY) {
    if (path === "/" || path === homePath) continue;
    const item = byPath.get(path);
    if (item) quick.push(item);
    if (quick.length >= maxQuick) break;
  }
  return { home, quick };
}

/**
 * Main module shortcuts for admin/manager bottom bar.
 * Sub-pages stay in the sticky chip bar / More drill-down — not here.
 */
export function pickBottomModuleItems(navGroups, maxQuick = 3, role) {
  const home = homeNavItem(flattenVisibleNav(navGroups), role);
  const byId = new Map((navGroups || []).map((g) => [g.id, g]));
  const quick = [];

  for (const moduleId of BOTTOM_MODULE_PRIORITY) {
    if (moduleId === "overview") continue;
    const group = byId.get(moduleId);
    if (!group?.items?.length) continue;
    const byPath = new Map(group.items.map((n) => [n.to, n]));
    const preferred = (MODULE_LANDING_PATHS[moduleId] || []).map((p) => byPath.get(p)).find(Boolean);
    const landing = preferred || group.items[0];
    if (!landing) continue;
    quick.push({
      ...landing,
      label: group.label,
      shortLabel: MODULE_SHORT_LABELS[moduleId] || group.label,
      iconKey: MODULE_ICON_KEYS[moduleId] || landing.iconKey,
      moduleId,
      matchTos: group.items.map((n) => n.to),
    });
    if (quick.length >= maxQuick) break;
  }

  return { home, quick };
}

function flattenVisibleNav(navGroups) {
  return (navGroups || []).flatMap((g) => g.items || []);
}

export function isStandaloneDisplay() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches
    || window.navigator.standalone === true
  );
}
